import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { itemTypesFor, type ItemType } from '@/utils/itemType'

interface ItemTypeFilterProps {
  dark: boolean
  /** Conjunto vacío = «Todos» (sin filtro). */
  value: Set<ItemType>
  onChange: (v: Set<ItemType>) => void
  /** Modo AVP: 2 opciones (QARF/ARF). Default false = estándar Q/P/X. */
  avp?: boolean
}

export function ItemTypeFilter({ dark, value, onChange, avp = false }: ItemTypeFilterProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const types = itemTypesFor(avp)

  const labelOf = (ty: ItemType) => {
    switch (ty) {
      case 'Q':
      case 'QARF': return t('common.itemType.chemicals')
      case 'X':    return t('common.itemType.semi')
      case 'P':
      case 'ARF':
      default:     return t('common.itemType.product')
    }
  }

  const buttonLabel =
    value.size === 0 ? t('common.itemType.all')
      : value.size === 1 ? labelOf([...value][0])
        : t('common.itemType.selected', { count: value.size })

  function toggle(ty: ItemType) {
    const next = new Set(value)
    next.has(ty) ? next.delete(ty) : next.add(ty)
    onChange(next)
  }

  const card = dark
    ? 'bg-slate-900 border-white/10 text-slate-200'
    : 'bg-white border-slate-200 text-slate-700'
  const fieldBtn = dark
    ? 'bg-slate-800 border-white/10 text-slate-100 hover:bg-slate-700'
    : 'bg-white border-slate-200 text-slate-800 hover:bg-slate-50 shadow-sm'
  const activeBtn = dark
    ? 'bg-sky-500/15 border-sky-500/60 text-sky-200'
    : 'bg-sky-50 border-sky-300 text-sky-700'
  const row = 'flex items-center gap-2 text-[12px] cursor-pointer px-1.5 py-1 rounded-md'

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`text-[12px] px-2.5 py-1.5 rounded-lg border outline-none transition-colors w-full flex items-center justify-between gap-2 cursor-pointer ${
          value.size > 0 ? activeBtn : fieldBtn
        }`}
      >
        <span className="truncate">{buttonLabel}</span>
        <span className="opacity-50 text-[10px] shrink-0">▾</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className={`absolute left-0 top-full mt-1 z-50 w-52 rounded-xl border shadow-2xl p-2 ${card}`}>
            <label className={row}>
              <input type="checkbox" checked={value.size === 0} onChange={() => onChange(new Set())} />
              <span>{t('common.itemType.all')}</span>
            </label>
            <div className={`my-1 border-t ${dark ? 'border-white/10' : 'border-slate-100'}`} />
            {types.map(ty => (
              <label key={ty} className={row}>
                <input type="checkbox" checked={value.has(ty)} onChange={() => toggle(ty)} />
                <span>{labelOf(ty)}</span>
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
